import { TElement } from '@udecode/plate';
import { API } from 'src/api';
import { BoardItem } from 'src/features/kanban/component/Board/types';
import { EstimatedTimeObject, TaskT } from 'src/features/tasks/types/tasks';
import { getMemberDetails } from 'src/utils/getMemberDetails';

export const useBoardService = () => {
  async function fetchPublicBoardDetails({
    id,
    filters,
    boardId,
  }: {
    id: string;
    isPublicView?: boolean;
    boardId?: string;
    filters: {
      filter: string;
      behaviour: 'is' | 'is_not' | 'is_set' | 'is_not_set';
      property: string;
    }[];
  }) {
    try {
      const response = await API.post(`/files/public?id=${id}&view=${boardId}`, {
        filters,
      });
      if (response.data.success) {
        return formatBoardData(response.data.data);
      } else if (!response.data.success && response.data.message === 'Unauthorised access') {
        return response.data.message;
      }
    } catch (error) {
      console.error('errrpr', error);
      return null;
    }
  }

  async function fetchBoardDetails({
    id,
    isPublicView = false,
    filters,
  }: {
    id: string;
    isPublicView?: boolean;
    filters: {
      filter: string;
      behaviour: 'is' | 'is_not' | 'is_set' | 'is_not_set';
      property: string;
    }[];
  }) {
    try {
      const response = await API.post(`/board?id=${id}`, {
        board: id,
        filters,
      });
      if (response.data.success) {
        return formatBoardData(response.data.data);
      } else if (!response.data.success && response.data.message === 'Unauthorised access') {
        return response.data.message;
      }
    } catch (error) {
      console.error('errrpr', error);
      return null;
    }
  }

  async function fetchBoardById({
    id,
    isPublicView = false,
  }: {
    id: string;
    isPublicView?: boolean;
  }) {
    try {
      const response = await API.get(`/board${isPublicView ? '/public' : ''}?id=${id}`);

      if (response.data.success) {
        return formatBoardData(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function deleteColumn({ colId }: { colId: string }) {
    try {
      const response = await API.delete(`/board/column?id=${colId}`);
      if (response.data.success) {
        return formatBoardData(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function addColumn({
    boardId,
    name,
    color,
  }: {
    boardId: string;
    name: string;
    color: string;
  }) {
    try {
      const response = await API.post('/board/column', {
        board: boardId,
        name: name,
        color: color,
      });
      if (response.data.success) {
        return formatBoardData(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function addTask(props: {
    boardId: string;
    colId: string;
    type: string;
    taskName: TElement[];
    assigner?: string;
    assignee?: string[];
    duplicate?: string[];
    startDate?: string;
    endDate?: string;
    description?: any[];
    priority?: string;
    label?: any;
    creation?: string;
    attachments?: any[];
    estimates?: EstimatedTimeObject[];
    top?: boolean;
    selectedTemplate?: string;
  }) {
    try {
      const payload = {
        board: props.boardId,
        column: props.colId,
        name: props.taskName,
        type: props.type,
        assigner: props.assigner,
        assignee: props.assignee,
        duplicate: props.duplicate,
        start_date: props?.startDate,
        end_date: props?.endDate,
        description: props.description,
        priority: props.priority,
        label: props.label,
        creation: props.type === 'creation' ? props.creation : undefined,
        attachments: props.attachments,
        estimates: props.estimates,
        top: props.top,
        template: props.selectedTemplate,
      };

      const response = await API.post('/board/task', payload);
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function deleteTask({ id }: { id: string }) {
    try {
      const response = await API.delete(`/board/task?id=${id}`);
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function duplicateTask({ id }: { id: string }) {
    try {
      const response = await API.post('board/task/duplicate', { task: id });
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function updateColumn({
    colProperties,
    colKey,
    id,
  }: {
    colProperties: { name?: string; color?: string };
    colKey: 'name' | 'color';
    id: string;
  }) {
    try {
      const response = await API.put('/board/column/edit', { id, [colKey]: colProperties[colKey] });
      if (response.data.success) {
        return formatBoardData(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function updateTaskProperties({
    taskId,
    properties,
  }: {
    taskId: string;
    properties: {
      assignee?: string[];
      assigner?: string;
      start_date?: string;
      end_date?: string;
      [key: string]: any;
    };
  }) {
    try {
      const payload: any = { ...properties };

      payload.id = taskId;
      const response = await API.put('/board/task', payload);
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function fetchTask({ id }: { id: string }) {
    try {
      const response = await API.get(`/board/task?id=${id}`);
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function addComment({ content, itemId, text, tags, boardId }) {
    try {
      const response = await API.post(`board/comment`, {
        board: boardId,
        text: text,
        task: itemId,
        data: content,
        tags: tags,
      });
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function updateComment({ content, commentId, tags, text }) {
    try {
      const response = await API.put(`board/comment`, {
        comment: commentId,
        data: content,
        text: text,
        tags: tags,
      });
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function deleteComment({ commentId }) {
    try {
      const response = await API.delete(`board/comment?id=${commentId}`);
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function moveTask({
    boardId,
    taskId,
    sourceColId,
    destColId,
    newItemIndex,
  }: {
    boardId: string;
    taskId: string;
    sourceColId: string;
    destColId: string;
    newItemIndex: number;
  }) {
    try {
      const response = await API.put('board/column', {
        board: boardId,
        task_move: {
          id: taskId,
          src_col: sourceColId,
          dest_col: destColId,
          index: newItemIndex,
        },
      });
      if (response.data.success) {
        return true;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function useLabelsForBoards({
    id,
    isTaskTemplateId,
  }: {
    id: string;
    isTaskTemplateId?: boolean;
  }) {
    try {
      let query = `board/label/${id}`;
      if (isTaskTemplateId) query += `?isTaskTemplateId=1`;
      const response = await API.get(query);
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function editLabel({
    id,
    label,
    color,
    board,
    task,
  }: {
    id: string;
    board: string;
    label: string;
    color: string;
    task: string;
  }) {
    try {
      const response = await API.put(`/board/label`, {
        id,
        board,
        label,
        color,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function deleteLabel({ labelId, boardId }: { labelId: string; boardId: string }) {
    try {
      const response = await API.delete(`/board/label?board=${boardId}&id=${labelId}`);
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function useSelectablesById({ id, isTaskTemplateId }) {
    try {
      let query = `board/custom-label/${id}`;
      if (isTaskTemplateId) query += `?isTaskTemplateId=1`;
      const response = await API.get(query);
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function addFile(taskId: string, fileName: string, filePath: string) {
    try {
      const response = await API.post('/board/task/file', {
        task: taskId,
        add: [{ name: fileName, url: filePath }],
      });
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function removeFile(taskId: string, filePath: string) {
    try {
      const response = await API.post('/board/task/file', {
        task: taskId,
        remove: [filePath],
      });
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function updateColumnOrder({
    boardId,
    columnIds,
  }: {
    boardId: string;
    columnIds: string[];
  }) {
    try {
      const response = await API.put('/board/column', { board: boardId, columns: columnIds });
      if (response.data.success) {
        return formatBoardData(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function moveTaskToAnotherBoard({ taskIds, destBoardId }) {
    try {
      const response = await API.post('board/task/move', {
        file: destBoardId,
        tasks: taskIds,
      });
      if (response.data.success) {
        return true;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function deleteTasks({ taskIds }) {
    try {
      const response = await API.post('board/task/bulk/delete', {
        tasks: taskIds,
      });
      console.log(response, taskIds);
      if (response.data.success) {
        return true;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function addCover({ boardId, coverImage }: { boardId: string; coverImage: string }) {
    try {
      const response = await API.put(`/board?id=${boardId}`, {
        showCover: true,
        coverImage,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function disableCover({ boardId }: { boardId: string }) {
    try {
      const response = await API.put(`/board?id=${boardId}`, {
        showCover: false,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function togglePublicView(boardId: string, makePublic = true) {
    try {
      const response = await API.put('/board/public', {
        board: boardId,
        is_public: makePublic,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function toggleUpovteTask(taskId: string, upvote = true) {
    try {
      const response = await API.put('/board/public', {
        task: taskId,
        vote: upvote ? 1 : -1,
      });
      return response;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function searchBoard({ boardId, search }: { boardId: string; search?: string }) {
    try {
      const response = await API.post('/board/search', {
        board: boardId,
        search: search,
      });
      if (response.data.success) {
        return formatBoardData(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function fetchTimer() {
    try {
      const response = await API.get('/timer/user');

      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  async function markTaskAsComplete(taskId: string, complete) {
    try {
      const response = await API.post('/board/task/complete', {
        id: taskId,
        completed: complete,
      });
      if (response.data.success) {
        return formatTask(response.data.data);
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function getSubtasks(taskId: string) {
    try {
      const response = await API.get(`/board/task/subtask?id=${taskId}`);
      if (response.data.success) {
        return response.data.data.map((obj) => formatTask(obj));
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async function setShowQuickFilter(value: boolean) {
    try {
      const response = await API.put('/settings/preferences', {
        showQuickFilter: value,
      });

      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
    return null;
  }

  return {
    fetchBoardDetails,
    deleteColumn,
    addColumn,
    addTask,
    deleteTask,
    updateColumn,
    updateTaskProperties,
    fetchTask,
    addComment,
    updateComment,
    deleteComment,
    moveTask,
    useLabelsForBoards,
    editLabel,
    deleteLabel,
    useSelectablesById,
    moveTaskToAnotherBoard,
    deleteTasks,
    disableCover,
    addCover,
    togglePublicView,
    toggleUpovteTask,
    fetchPublicBoardDetails,
    searchBoard,
    fetchTimer,
    setShowQuickFilter,
    updateColumnOrder,
    fetchBoardById,
    duplicateTask,
    addFile,
    removeFile,

    markTaskAsComplete,
    getSubtasks,
  };
};

export interface BoardType {
  columns: Column[];
  createdAt: string;
  createdBy: string | { firstname: string };
  sharedBy: string | { firstname: string };
  access: string;
  color?: string;
  icon?: {
    id?: any;
    type?: string;
  };
  default: boolean;
  coverImage: string;
  showCover: boolean;
  name: string;
  title: string;
  key: string;
  owner: {
    id: string;
    firstname: string;
    lastname: string;
    avatar: string;
    email: string;
    memberId: string;
  };
  shared: any[];
  status: string;
  updatedAt: string;
  id: string;
  fileId?: string;
  isPublicView: boolean;
  isPublic: boolean;
  publicId?: string;
  updatedBy: {
    avatar: string;
    email: string;
    memberId: string;
    name: string;
  };
  workspace: string;
}

export interface Column {
  items: BoardItem[];
  name: string;
  color: string;
  id: string;
}

export function formatBoardData(data): BoardType {
  return {
    ...data,
    id: data?._id,
    fileId: data?.fileId,
    publicId: data?.publicId,
    isPublic: data?.is_public,
    owner: data?.owner
      ? {
          id: data?.owner?._id,
          ...data?.owner,
        }
      : {},
    updatedBy: {
      ...data.updatedBy,
    },
    columns: data.columns?.map((obj) => ({
      ...obj,
      id: obj._id,
      items: obj.items.map((item) => formatTask(item)),
    })),
    fields: data?.fields || [],
  };
}

function formatTask(data): TaskT & { boardId: string; columnId: string } {
  return {
    ...data,
    boardId: data.board,
    fileId: data.file,
    approvalStatus:
      data?.approvers?.length > 0
        ? data?.approvers.filter((item) => item.status === 'approved').length >=
          data?.minimumApprovals
          ? 'approved'
          : data?.approvers.filter((item) => item.status === 'rejected').length >
            data?.approvers.length - data?.minimumApprovals
          ? 'rejected'
          : 'pending'
        : undefined,

    columnId: data.column,

    assignee: data?.assignee?.map((id) => getMemberDetails(id)) || [],
    id: data._id,
    assignDate:
      data.start_date && data.end_date
        ? [data.start_date, data.end_date]
        : data.end_date
        ? [data.end_date]
        : [],
    // commentsCount: data.comments,
    subtasks: data.subtasks?.map((task) => formatTask(task)) || [],
    epic: data?.epicRef || null,
    storyPoint: data?.storyPoint || null,
  };
}
